export const INTERP_MIN_DELAY = 55
export const INTERP_MAX_DELAY = 120
export const INTERP_DELAY_FACTOR = 1.15
export const INTERP_DEFAULT_INTERVAL = 100
export const INTERP_SNAP_DISTANCE = 16
export const INTERP_SPLINE_GAP_MS = 250
const BUFFER_MAX = 16

const hermite = (c0: number, c1: number, c2: number, c3: number, t0: number, t1: number, t2: number, t3: number, s: number, h: number) => {
  const m1 = (c2 - c0) / (t2 - t0 || 1)
  const m2 = (c3 - c1) / (t3 - t1 || 1)
  const s2 = s * s; const s3 = s2 * s
  return (2 * s3 - 3 * s2 + 1) * c1 +
    (s3 - 2 * s2 + s) * h * m1 +
    (-2 * s3 + 3 * s2) * c2 +
    (s3 - s2) * h * m2
}

interface Vec3Like { x: number, y: number, z: number }

export interface InterpolationTarget {
  position: Vec3Like
  rotation: { y: number }
  playerObject?: { skin?: { head?: { rotation: { x: number, y: number } } } }
}

interface Snapshot { t: number, x: number, y: number, z: number, yaw: number, pitch: number, hyaw: number, hpitch: number }

export interface EntityUpdate {
  x?: number, y?: number, z?: number
  yaw?: number
  pitch?: number
  headRelYaw?: number
  headPitch?: number
}

export class EntityInterpolation {
  private readonly buf: Snapshot[] = []
  private readonly cur: Snapshot = { t: 0, x: 0, y: 0, z: 0, yaw: 0, pitch: 0, hyaw: 0, hpitch: 0 }
  private readonly out: Snapshot = { t: 0, x: 0, y: 0, z: 0, yaw: 0, pitch: 0, hyaw: 0, hpitch: 0 }
  private posActive = false
  private yawActive = false
  private pitchActive = false
  private headActive = false
  private smoothedInterval = INTERP_DEFAULT_INTERVAL
  private lastPushT = 0

  private static unwrap (from: number, to: number) {
    const da = (to - from) % (Math.PI * 2)
    return from + (2 * da % (Math.PI * 2) - da)
  }

  update (now: number, u: EntityUpdate, forceSnap = false) {
    let snap = forceSnap
    if (u.x !== undefined && u.y !== undefined && u.z !== undefined) {
      if (this.posActive) {
        const dx = u.x - this.cur.x; const dy = u.y - this.cur.y; const dz = u.z - this.cur.z
        if (dx * dx + dy * dy + dz * dz > INTERP_SNAP_DISTANCE * INTERP_SNAP_DISTANCE) snap = true
      }
      this.cur.x = u.x; this.cur.y = u.y; this.cur.z = u.z
      this.posActive = true
    }
    if (u.yaw !== undefined) { this.cur.yaw = EntityInterpolation.unwrap(this.cur.yaw, u.yaw); this.yawActive = true }
    if (u.pitch !== undefined) { this.cur.pitch = u.pitch; this.pitchActive = true }
    if (u.headRelYaw !== undefined) { this.cur.hyaw = EntityInterpolation.unwrap(this.cur.hyaw, u.headRelYaw); this.headActive = true }
    if (u.headPitch !== undefined) { this.cur.hpitch = EntityInterpolation.unwrap(this.cur.hpitch, u.headPitch); this.headActive = true }

    if (!snap && this.buf.length > 0) {
      const l = this.buf.at(-1)!
      if (this.cur.x === l.x && this.cur.y === l.y && this.cur.z === l.z &&
        this.cur.yaw === l.yaw && this.cur.pitch === l.pitch &&
        this.cur.hyaw === l.hyaw && this.cur.hpitch === l.hpitch) {
        return
      }
    }

    if (snap) {
      this.buf.length = 0
      this.smoothedInterval = INTERP_DEFAULT_INTERVAL
    } else if (this.lastPushT) {
      const interval = now - this.lastPushT
      if (interval > 0) this.smoothedInterval = this.smoothedInterval * 0.6 + interval * 0.4
    }
    this.buf.push({ ...this.cur, t: now })
    this.lastPushT = now
    if (this.buf.length > BUFFER_MAX) this.buf.shift()
  }

  getRenderDelay () {
    return Math.max(INTERP_MIN_DELAY, Math.min(this.smoothedInterval * INTERP_DELAY_FACTOR, INTERP_MAX_DELAY))
  }

  private sampleSnapshot (now: number): Snapshot | null {
    const { buf } = this
    if (buf.length === 0) return null
    if (buf.length === 1) return buf[0]

    const rt = now - this.getRenderDelay()
    if (rt <= buf[0].t) return buf[0]
    const last = buf.at(-1)!
    if (rt >= last.t) return last

    let i = 0
    while (i < buf.length - 1 && buf[i + 1].t <= rt) i++
    const p1 = buf[i]; const p2 = buf[i + 1]
    const h = p2.t - p1.t
    if (h <= 0) return p2
    const s = (rt - p1.t) / h

    const o = this.out
    o.t = rt
    if (h > INTERP_SPLINE_GAP_MS) {
      const lin = (a: number, b: number) => a + (b - a) * s
      o.x = lin(p1.x, p2.x); o.y = lin(p1.y, p2.y); o.z = lin(p1.z, p2.z)
      o.yaw = lin(p1.yaw, p2.yaw); o.pitch = lin(p1.pitch, p2.pitch)
      o.hyaw = lin(p1.hyaw, p2.hyaw); o.hpitch = lin(p1.hpitch, p2.hpitch)
      return o
    }

    const p0 = buf[i - 1] ?? p1
    const p3 = buf[i + 2] ?? p2
    const cr = (c0: number, c1: number, c2: number, c3: number) => hermite(c0, c1, c2, c3, p0.t, p1.t, p2.t, p3.t, s, h)
    o.x = cr(p0.x, p1.x, p2.x, p3.x)
    o.y = cr(p0.y, p1.y, p2.y, p3.y)
    o.z = cr(p0.z, p1.z, p2.z, p3.z)
    o.yaw = cr(p0.yaw, p1.yaw, p2.yaw, p3.yaw)
    o.pitch = cr(p0.pitch, p1.pitch, p2.pitch, p3.pitch)
    o.hyaw = cr(p0.hyaw, p1.hyaw, p2.hyaw, p3.hyaw)
    o.hpitch = cr(p0.hpitch, p1.hpitch, p2.hpitch, p3.hpitch)
    return o
  }

  apply (now: number, obj: InterpolationTarget) {
    const s = this.sampleSnapshot(now)
    if (!s) return
    if (this.posActive) { obj.position.x = s.x; obj.position.y = s.y; obj.position.z = s.z }
    if (this.yawActive) obj.rotation.y = s.yaw
    if (this.headActive) {
      const head = obj.playerObject?.skin?.head
      if (head) { head.rotation.y = s.hyaw; head.rotation.x = s.hpitch }
    }
  }

  sample (now: number): { pose: Snapshot | null, hasPosition: boolean } {
    return { pose: this.sampleSnapshot(now), hasPosition: this.posActive }
  }
}

export type InterpolatedPose = Snapshot

// Live snapshot interpolation (above) must lag by a render delay because it can't see
// future packets. A replay knows the whole path up front, so it can interpolate between
// the points bracketing the current playback time with no added latency.

export const INTERP_LOOKAHEAD_DELAY = 0

export interface TimelinePoint { t: number, x: number, y: number, z: number, yaw: number, pitch: number }

const copyPoint = (dst: TimelinePoint, src: TimelinePoint) => {
  dst.t = src.t; dst.x = src.x; dst.y = src.y; dst.z = src.z
  dst.yaw = src.yaw; dst.pitch = src.pitch
}

// Samples a time-sorted timeline at recorded-time `rt`. `hint` is the last returned index,
// keeping repeated forward sampling O(1); it also walks backwards to support seeking.
// Writes into `out`, returns the new cursor.
export const sampleTimeline = (pts: TimelinePoint[], rt: number, hint: number, out: TimelinePoint): number => {
  const n = pts.length
  if (n === 0) return 0
  if (rt <= pts[0].t) { copyPoint(out, pts[0]); return 0 }
  if (rt >= pts[n - 1].t) { copyPoint(out, pts[n - 1]); return n - 1 }

  let i = hint
  if (i < 0) i = 0
  if (i > n - 2) i = n - 2
  while (i < n - 1 && pts[i + 1].t <= rt) i++
  while (i > 0 && pts[i].t > rt) i--

  const p1 = pts[i]; const p2 = pts[i + 1]
  const h = p2.t - p1.t
  const s = h > 0 ? (rt - p1.t) / h : 0
  out.t = rt

  const dx = p2.x - p1.x; const dy = p2.y - p1.y; const dz = p2.z - p1.z
  if (dx * dx + dy * dy + dz * dz > INTERP_SNAP_DISTANCE * INTERP_SNAP_DISTANCE) {
    // teleport between two adjacent samples — step, don't slide through the world
    const p = s < 0.5 ? p1 : p2
    out.x = p.x; out.y = p.y; out.z = p.z; out.yaw = p.yaw; out.pitch = p.pitch
    return i
  }

  if (h > INTERP_SPLINE_GAP_MS) {
    const lin = (a: number, b: number) => a + (b - a) * s
    out.x = lin(p1.x, p2.x); out.y = lin(p1.y, p2.y); out.z = lin(p1.z, p2.z)
    out.yaw = lin(p1.yaw, p2.yaw); out.pitch = lin(p1.pitch, p2.pitch)
    return i
  }

  const p0 = pts[i - 1] ?? p1; const p3 = pts[i + 2] ?? p2
  const cr = (c0: number, c1: number, c2: number, c3: number) => hermite(c0, c1, c2, c3, p0.t, p1.t, p2.t, p3.t, s, h)
  out.x = cr(p0.x, p1.x, p2.x, p3.x)
  out.y = cr(p0.y, p1.y, p2.y, p3.y)
  out.z = cr(p0.z, p1.z, p2.z, p3.z)
  out.yaw = cr(p0.yaw, p1.yaw, p2.yaw, p3.yaw)
  out.pitch = cr(p0.pitch, p1.pitch, p2.pitch, p3.pitch)
  return i
}
